import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Camera, Bell, Lock, ShieldCheck, Check, Sparkles, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../../packages/auth-client';
import { apiClient, getErrorMessage, resolveAvatarUrl } from '../../packages/api-client';
import { Button, PageHeader } from '../../packages/ui';
import { TrustedDevices } from './TrustedDevices';
import { EmailDigestSettings } from '../../components/EmailDigestSettings';
import { AssistantSettings } from '../../components/AssistantSettings';
import { DeleteAccountPanel } from '../../components/DeleteAccountPanel';
import { usePageTitle } from '../../hooks/usePageTitle';
import { PageSkeleton } from '../../components/Skeleton';
// LocaleSelector intentionally not imported — the component exists but
// the rest of the app has no useTranslation / translation files behind it,
// so exposing the picker would advertise a feature that doesn't work
// (UAT 10.4). Restore this import + the Language tab below when real
// i18next integration ships.

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserProfile {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
}

type Tab = 'info' | 'password' | 'avatar' | 'devices' | 'notifications' | 'assistant' | 'delete';

/**
 * Every ?tab= value /profile accepts. A deep-link contract: emails, the
 * calendar OAuth callback and /settings link to these, so none may be renamed.
 */
export const PROFILE_TAB_IDS: readonly Tab[] = ['info', 'password', 'avatar', 'devices', 'notifications', 'assistant', 'delete'];

function parseTab(raw: string | null): Tab {
    return raw !== null && (PROFILE_TAB_IDS as readonly string[]).includes(raw) ? (raw as Tab) : 'info';
}

// ─── Avatar helpers ───────────────────────────────────────────────────────────

function getInitials(first: string, last: string): string {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function AvatarDisplay({
    avatarUrl,
    firstName,
    lastName,
    size = 80,
}: {
    avatarUrl?: string;
    firstName: string;
    lastName: string;
    size?: number;
}) {
    // A stored avatar_url can outlive its file (e.g. an avatar saved to
    // ephemeral storage before S3, lost on redeploy → the /uploads route now
    // 302→403s). Without onError that surfaces as a broken-image icon; instead
    // fall through to the initials avatar. Reset on URL change so a fresh
    // upload gets a clean attempt.
    const [imgFailed, setImgFailed] = useState(false);
    useEffect(() => setImgFailed(false), [avatarUrl]);

    if (avatarUrl && !imgFailed) {
        return (
            <img
                src={resolveAvatarUrl(avatarUrl)}
                alt="Profile photo"
                onError={() => setImgFailed(true)}
                style={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid var(--accent)',
                }}
            />
        );
    }
    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--on-accent)',
                fontSize: size * 0.35,
                fontWeight: 700,
                border: '3px solid var(--accent)',
                flexShrink: 0,
            }}
        >
            {getInitials(firstName || '?', lastName || '?')}
        </div>
    );
}

// ─── Email field with self-service change flow ────────────────────────────────

/**
 * EmailField renders the read-only email plus a "Change email" toggle that
 * opens a sub-form (current password + new email). On submit it kicks off
 * the verification flow on the backend; the user must click the link sent
 * to the NEW mailbox to commit. Successful submission shows an
 * inline confirmation — we never tell the form whether the new address is
 * taken beyond the explicit 409 path that the backend returns.
 */
function EmailField({ email }: { email: string }) {
    // Impersonators don't have the target user's password, and the
    // self-service flow requires it. Hide the form and point them at the
    // admin Users page, which lets a superadmin change the email directly
    // (no password required, with session revocation + dual notification).
    const { isImpersonating } = useAuth();
    const [open, setOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setSubmitting(true);
        setError('');
        try {
            await apiClient.post('/users/me/email/change-request', {
                current_password: currentPassword,
                new_email: newEmail,
            });
            setSent(true);
            setCurrentPassword('');
            setNewEmail('');
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setOpen(false);
        setSent(false);
        setError('');
        setCurrentPassword('');
        setNewEmail('');
    };

    return (
        <div className="form-group">
            <label className="form-label">Email</label>
            <div className="profile-email-row">
                <input
                    className="form-input"
                    value={email}
                    readOnly
                    style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
                {!open && !isImpersonating && (
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => { setOpen(true); setSent(false); setError(''); }}
                    >
                        Change email
                    </Button>
                )}
            </div>
            {isImpersonating && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                    You are impersonating this user. To change their email, use the Admin → Users page (the self-service flow requires the target user&apos;s password).
                </span>
            )}

            {open && (
                <div
                    style={{
                        marginTop: 'var(--space-3)',
                        padding: 'var(--space-4)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--color-bg-subtle, transparent)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-4)',
                    }}
                >
                    {sent ? (
                        <>
                            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--ok)', fontWeight: 500, margin: 0 }}>
                                Check your new email inbox for a confirmation link. The link expires in 1 hour. You will be signed out once the change is confirmed.
                            </p>
                            <div>
                                <Button type="button" variant="secondary" size="sm" onClick={reset}>
                                    Done
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', margin: 0 }}>
                                We&apos;ll send a confirmation link to the new address. Your existing sessions will be signed out once you click it.
                            </p>
                            <div className="form-group" style={{ margin: 0 }}>
                                <label className="form-label" htmlFor="email-change-current-password">Current password</label>
                                <input
                                    className="form-input"
                                    id="email-change-current-password"
                                    type="password"
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                />
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                                <label className="form-label" htmlFor="email-change-new-email">New email</label>
                                <input
                                    className="form-input"
                                    id="email-change-new-email"
                                    type="email"
                                    value={newEmail}
                                    onChange={e => setNewEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                />
                            </div>
                            {error && <p className="form-error" style={{ margin: 0 }}>{error}</p>}
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    disabled={submitting || !currentPassword || !newEmail}
                                    onClick={handleSubmit}
                                >
                                    {submitting ? 'Sending…' : 'Send confirmation link'}
                                </Button>
                                <Button type="button" variant="secondary" size="sm" onClick={reset} disabled={submitting}>
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Tab: Profile Info ────────────────────────────────────────────────────────

function InfoTab({ profile, onSaved }: { profile: UserProfile; onSaved: (p: UserProfile) => void }) {
    const { refreshUser } = useAuth();
    const [firstName, setFirstName] = useState(profile.first_name);
    const [lastName, setLastName] = useState(profile.last_name);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess(false);
        try {
            const { data } = await apiClient.put<UserProfile>('/users/me', {
                first_name: firstName,
                last_name: lastName,
            });
            onSaved(data);
            await refreshUser();
            setSuccess(true);
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 440 }}>
            <EmailField email={profile.email} />

            <div className="profile-name-fields">
                <div className="form-group">
                    <label className="form-label">First name</label>
                    <input
                        className="form-input"
                        id="profile-first-name"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        required
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">Last name</label>
                    <input
                        className="form-input"
                        id="profile-last-name"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        required
                    />
                </div>
            </div>

            {error && <p className="form-error">{error}</p>}
            {success && (
                <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-sm)', color: 'var(--ok)', fontWeight: 500 }}>
                    <Check size={16} aria-hidden="true" /> Profile updated successfully!
                </p>
            )}

            <div>
                <Button type="submit" variant="primary" size="md" disabled={saving}>
                    {saving ? 'Saving…' : 'Save changes'}
                </Button>
            </div>
        </form>
    );
}

// ─── Tab: Change Password ─────────────────────────────────────────────────────

function PasswordTab() {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (next.length < 8) {
            setError('New password must be at least 8 characters.');
            return;
        }
        if (next !== confirm) {
            setError('New passwords do not match.');
            return;
        }

        setSaving(true);
        try {
            await apiClient.post('/users/me/password', {
                current_password: current,
                new_password: next,
            });
            setSuccess(true);
            setCurrent(''); setNext(''); setConfirm('');
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 440 }}>
            <div className="form-group">
                <label className="form-label">Current password</label>
                <input
                    className="form-input"
                    id="current-password"
                    type="password"
                    value={current}
                    onChange={e => setCurrent(e.target.value)}
                    required
                    autoComplete="current-password"
                />
            </div>
            <div className="form-group">
                <label className="form-label">New password</label>
                <input
                    className="form-input"
                    id="new-password"
                    type="password"
                    value={next}
                    onChange={e => setNext(e.target.value)}
                    required
                    autoComplete="new-password"
                />
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                    Minimum 8 characters.
                </span>
            </div>
            <div className="form-group">
                <label className="form-label">Confirm new password</label>
                <input
                    className="form-input"
                    id="confirm-password"
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                />
            </div>

            {error && <p className="form-error">{error}</p>}
            {success && (
                <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-sm)', color: 'var(--ok)', fontWeight: 500 }}>
                    <Check size={16} aria-hidden="true" /> Password changed successfully!
                </p>
            )}

            <div>
                <Button type="submit" variant="primary" size="md" disabled={saving}>
                    {saving ? 'Updating…' : 'Update password'}
                </Button>
            </div>
        </form>
    );
}

// ─── Tab: Avatar ──────────────────────────────────────────────────────────────

function AvatarTab({ profile, onSaved }: { profile: UserProfile; onSaved: (p: UserProfile) => void }) {
    const { refreshUser } = useAuth();
    const fileRef = useRef<HTMLInputElement>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setPreview(URL.createObjectURL(file));
        setSuccess(false);
        setError('');
    };

    const handleUpload = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) return;
        setUploading(true);
        setError('');
        setSuccess(false);
        try {
            const form = new FormData();
            form.append('avatar', file);
            const { data } = await apiClient.post<UserProfile>('/users/me/avatar', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            onSaved(data);
            await refreshUser();
            setSuccess(true);
            setPreview(null);
            if (fileRef.current) fileRef.current.value = '';
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', maxWidth: 440 }}>
            {/* Current / preview */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-5)' }}>
                {preview ? (
                    // Freshly-selected local file (blob: URL) — always loads.
                    <img
                        src={preview}
                        alt="Avatar preview"
                        style={{
                            width: 80, height: 80, borderRadius: '50%', objectFit: 'cover',
                            border: '3px solid var(--accent)',
                        }}
                    />
                ) : (
                    // Stored avatar (may be a dead link) — AvatarDisplay falls
                    // back to initials on load failure.
                    <AvatarDisplay
                        avatarUrl={profile.avatar_url}
                        firstName={profile.first_name}
                        lastName={profile.last_name}
                        size={80}
                    />
                )}
                <div>
                    <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>Profile photo</p>
                    <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 4 }}>
                        JPEG, PNG, GIF, or WebP · max 5 MB
                    </p>
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                <input
                    ref={fileRef}
                    id="avatar-file"
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={() => fileRef.current?.click()}
                >
                    Choose file
                </Button>
                {preview && (
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        disabled={uploading}
                        onClick={handleUpload}
                    >
                        {uploading ? 'Uploading…' : 'Upload photo'}
                    </Button>
                )}
            </div>

            {error && <p className="form-error">{error}</p>}
            {success && (
                <p style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-sm)', color: 'var(--ok)', fontWeight: 500 }}>
                    <Check size={16} aria-hidden="true" /> Avatar updated!
                </p>
            )}
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ProfilePage() {
    usePageTitle('Personal settings');
    const { user } = useAuth();
    // Tab state is URL-driven (?tab=assistant) so assistant briefing footers
    // can deep-link into settings. Unknown/missing values fall back to the
    // original 'info' default, so all pre-existing /profile links behave the same.
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = parseTab(searchParams.get('tab'));
    const setTab = (next: Tab) => setSearchParams({ tab: next }, { replace: true });
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    useEffect(() => {
        apiClient.get<UserProfile>('/users/me')
            .then(res => setProfile(res.data))
            .catch(err => setLoadError(getErrorMessage(err)))
            .finally(() => setLoading(false));
    }, []);

    // Tab order is intentional: identity first (Info, Photo), then the most-
    // hunted settings. "Email & Notifications" used to be labeled just
    // "Notifications" and sat fifth — UAT testers couldn't find the email-
    // digest controls. Renaming + promoting makes them one click in.
    const tabs: { id: Tab; label: string; icon: LucideIcon }[] = [
        { id: 'info', label: 'Profile Info', icon: User },
        { id: 'avatar', label: 'Photo', icon: Camera },
        { id: 'notifications', label: 'Email & Notifications', icon: Bell },
        { id: 'assistant', label: 'Assistant', icon: Sparkles },
        { id: 'password', label: 'Password', icon: Lock },
        { id: 'devices', label: 'Trusted Devices', icon: ShieldCheck },
        // Last, and the only destructive tab. Play requires an account created
        // in the app to be deletable without contacting support; the standalone
        // /delete-account route covers people who already uninstalled.
        { id: 'delete', label: 'Delete Account', icon: Trash2 },
    ];

    if (loading) {
        return <PageSkeleton />;
    }

    if (loadError || !profile) {
        return (
            <div style={{ padding: 'var(--space-8)', color: 'var(--danger)' }}>
                Failed to load profile: {loadError}
            </div>
        );
    }

    return (
        <div>
            <PageHeader
                breadcrumb={[{ label: 'Settings', to: '/settings' }, { label: 'Personal' }]}
                title="Personal settings"
                subtitle="Your own account. Changes here affect only you, not your workspace."
            />
            {/* Identity */}
            <div className="page-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                    <AvatarDisplay
                        avatarUrl={profile.avatar_url}
                        firstName={profile.first_name}
                        lastName={profile.last_name}
                        size={56}
                    />
                    <div>
                        <p className="page-title" style={{ fontSize: 'var(--font-size-xl)' }}>
                            {profile.first_name} {profile.last_name}
                        </p>
                        <p className="page-subtitle">{profile.email}</p>
                        {user?.roles && (
                            <div className="roles-list" style={{ marginTop: 'var(--space-2)' }}>
                                {user.roles.map(r => (
                                    <span key={r} className="badge badge-info">{r}</span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Card with tabs */}
            <div className="card profile-settings-card">
                {/* Keep every section visible, wrapping on narrower screens. */}
                <nav className="profile-tabs" aria-label="Personal settings sections">
                    {tabs.map(t => (
                        <button
                            key={t.id}
                            id={`profile-tab-${t.id}`}
                            aria-current={tab === t.id ? 'true' : undefined}
                            onClick={() => setTab(t.id)}
                            className="profile-tab"
                        >
                            <t.icon size={16} aria-hidden="true" />
                            <span className="profile-tab-label">{t.label}</span>
                        </button>
                    ))}
                </nav>

                {/* Tab content */}
                {tab === 'info' && (
                    <InfoTab
                        profile={profile}
                        onSaved={updated => setProfile(prev => prev ? { ...prev, ...updated } : prev)}
                    />
                )}
                {tab === 'password' && <PasswordTab />}
                {tab === 'avatar' && (
                    <AvatarTab
                        profile={profile}
                        onSaved={updated => setProfile(prev => prev ? { ...prev, ...updated } : prev)}
                    />
                )}
                {tab === 'devices' && <TrustedDevices />}
                {tab === 'notifications' && <EmailDigestSettings />}
                {tab === 'assistant' && <AssistantSettings />}
                {tab === 'delete' && <DeleteAccountPanel />}
            </div>
        </div>
    );
}
